import React, { useState, useMemo } from "react";
import {
  MobileOutlined,
  TabletOutlined,
  LaptopOutlined,
  DesktopOutlined,
  PlusOutlined,
  MinusOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import InfoIcon from "../InfoIcon";
import ScreenSizeModeSelector from "./ScreenSizeModeSelector";
import { typography, surfaces, borders, textColors, interactive } from "../../../design-system/tokens";

/**
 * LayoutByLayoutField - ByLayout pattern for page/component layouts
 * 
 * Supports three editing modes (auto-detected):
 * - ONE (Unified): All breakpoints share the same layout
 * - TWO (Phone vs Desktop): XS+S share one layout, M+L+XL share another
 * - ALL (Custom): Each breakpoint has independent layouts
 * 
 * Each layout value is a JSON array like [1, 2, 1] representing components per row.
 */

const BREAKPOINTS = [
  { key: "extra_small", label: "XS", icon: <MobileOutlined />, group: "phone" },
  { key: "small", label: "S", icon: <TabletOutlined />, group: "phone" },
  { key: "medium", label: "M", icon: <LaptopOutlined />, group: "desktop" },
  { key: "large", label: "L", icon: <DesktopOutlined />, group: "desktop" },
  { key: "extra_large", label: "XL", icon: <DesktopOutlined />, group: "desktop" },
];

// Helper to detect which mode we're in based on value equality
const detectMode = (value) => {
  if (!value) return "one";
  
  const vals = BREAKPOINTS.map(bp => JSON.stringify(value[bp.key] || []));
  const allSame = vals.every(v => v === vals[0]);
  
  if (allSame) return "one";
  
  const phoneVal = vals[0]; // XS
  const desktopVal = vals[2]; // M
  const phoneMatch = vals[0] === vals[1]; // XS === S
  const desktopMatch = vals[2] === vals[3] && vals[3] === vals[4]; // M === L === XL
  const twoGroups = phoneMatch && desktopMatch && phoneVal !== desktopVal;
  
  if (twoGroups) return "two";
  
  return "all";
};

// Helper to parse layout array from value
const parseLayout = (val) => {
  if (!val) return [1];
  if (Array.isArray(val)) return val.length > 0 ? val : [1];
  try {
    const parsed = JSON.parse(val);
    return Array.isArray(parsed) && parsed.length > 0 ? parsed : [1];
  } catch {
    return [1];
  }
};

// Row editor component (reused from old LayoutField.js)
const RowEditor = ({ rows, onChange }) => {
  const clamp = (n) => Math.min(12, Math.max(1, n));
  
  const changeRowCount = (rowIdx, delta) => {
    const newRows = [...rows];
    newRows[rowIdx] = clamp(newRows[rowIdx] + delta);
    onChange(newRows);
  };
  
  const addRow = () => onChange([...rows, 1]);
  
  const removeRow = (rowIdx) => {
    if (rows.length <= 1) return; // Keep at least 1 row
    onChange(rows.filter((_, idx) => idx !== rowIdx));
  };
  
  return (
    <div className="space-y-3 mb-3">
      {rows.map((cols, rowIdx) => (
        <div key={rowIdx} className="flex items-center gap-2">
          <span className={`w-16 text-left font-medium ${typography.caption} ${textColors.primary}`}>
            {`Row ${rowIdx + 1}`}
          </span>
          
          <button
            type="button"
            onClick={() => changeRowCount(rowIdx, -1)}
            className={`p-1.5 rounded-l border ${borders.default} ${surfaces.tertiary} ${surfaces.hover} ${textColors.primary} transition-colors`}
            title="Remove column"
          >
            <MinusOutlined />
          </button>
          
          <div className={`flex flex-1 border ${borders.default} ${surfaces.secondary} rounded overflow-hidden p-2`}>
            <div className="flex items-center justify-center w-full gap-1.5">
              {Array.from({ length: cols }).map((_, idx) => (
                <div
                  key={idx}
                  className={`flex-1 h-12 bg-gradient-to-br from-blue-100 to-blue-200 dark:from-blue-900/40 dark:to-blue-800/40 border ${borders.default} rounded-lg shadow-sm flex items-center justify-center`}
                >
                  <div className={`${typography.caption} ${textColors.tertiary} font-medium`}>
                    {idx + 1}
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          <button
            type="button"
            onClick={() => changeRowCount(rowIdx, 1)}
            className={`p-1.5 rounded-r border ${borders.default} ${surfaces.tertiary} ${surfaces.hover} ${textColors.primary} transition-colors`}
            title="Add column"
          >
            <PlusOutlined />
          </button>
          
          {rows.length > 1 && (
            <button
              type="button"
              onClick={() => removeRow(rowIdx)}
              className={`p-1.5 rounded border ${borders.default} ${surfaces.tertiary} ${surfaces.hover} ${textColors.primary} transition-colors`}
              title="Delete row"
            >
              <DeleteOutlined />
            </button>
          )}
        </div>
      ))}
      
      <button
        type="button"
        onClick={addRow}
        className={`${typography.button} ${interactive.link} ${interactive.linkHover} flex items-center gap-1 transition-colors`}
      >
        <PlusOutlined className="text-xs" />
        <span>Add Row</span>
      </button>
    </div>
  );
};

export const LayoutByLayoutField = ({ value, onChange, label, help, infoText }) => {
  const mode = useMemo(() => detectMode(value), [value]);
  const [activeMode, setActiveMode] = useState(mode);
  const [activeBP, setActiveBP] = useState("extra_small");
  
  // Get the current layout for editing based on mode
  const getCurrentLayout = (bpKey) => {
    if (!value || !value[bpKey]) return [1];
    return parseLayout(value[bpKey]);
  };
  
  // Update layout for one or more breakpoints
  const updateLayout = (newRows, targetBPs) => {
    const updated = { 
      id: value?.id,
      type: value?.type || "layout_by_layout",
      ...value 
    };
    targetBPs.forEach(bp => {
      updated[bp] = newRows;
    });
    onChange(updated);
  };
  
  // Handle mode switch
  const switchMode = (newMode) => {
    setActiveMode(newMode);
    
    if (newMode === "one") {
      // Apply current active BP layout to all
      const currentLayout = getCurrentLayout(activeBP);
      updateLayout(currentLayout, BREAKPOINTS.map(bp => bp.key));
    } else if (newMode === "two") {
      // Split into phone/desktop
      const phoneLayout = getCurrentLayout("extra_small");
      const desktopLayout = getCurrentLayout("medium");
      updateLayout(phoneLayout, ["extra_small", "small"]);
      updateLayout(desktopLayout, ["medium", "large", "extra_large"]);
    }
    // "all" mode - no auto-update needed
  };
  
  const renderEditor = () => {
    if (activeMode === "one") {
      const layout = getCurrentLayout("extra_small");
      return (
        <RowEditor
          rows={layout}
          onChange={(newRows) => updateLayout(newRows, BREAKPOINTS.map(bp => bp.key))}
        />
      );
    }
    
    if (activeMode === "two") {
      const phoneLayout = getCurrentLayout("extra_small");
      const desktopLayout = getCurrentLayout("medium");
      
      return (
        <div className="space-y-6">
          <div>
            <div className={`flex items-center gap-2 mb-3 pb-2 border-b ${borders.default}`}>
              <MobileOutlined className={`text-lg ${textColors.primary}`} />
              <span className={`font-semibold ${typography.bodySmall} ${textColors.primary}`}>
                Phone (XS, S)
              </span>
            </div>
            <RowEditor
              rows={phoneLayout}
              onChange={(newRows) => updateLayout(newRows, ["extra_small", "small"])}
            />
          </div>
          
          <div>
            <div className={`flex items-center gap-2 mb-3 pb-2 border-b ${borders.default}`}>
              <DesktopOutlined className={`text-lg ${textColors.primary}`} />
              <span className={`font-semibold ${typography.bodySmall} ${textColors.primary}`}>
                Desktop (M, L, XL)
              </span>
            </div>
            <RowEditor
              rows={desktopLayout}
              onChange={(newRows) => updateLayout(newRows, ["medium", "large", "extra_large"])}
            />
          </div>
        </div>
      );
    }
    
    // activeMode === "all"
    const layout = getCurrentLayout(activeBP);
    
    return (
      <div>
        {/* Breakpoint selector */}
        <div className="flex gap-2 mb-4 flex-wrap">
          {BREAKPOINTS.map((bp) => (
            <button
              type="button"
              key={bp.key}
              onClick={() => setActiveBP(bp.key)}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-lg border ${typography.caption} transition-all ${
                activeBP === bp.key
                  ? `${interactive.selectedBg} !text-white ${interactive.selectedBorder} shadow-sm`
                  : `${surfaces.tertiary} ${textColors.primary} ${borders.default} ${surfaces.hover}`
              }`}
            >
              <span className="text-base">{bp.icon}</span>
              <span className="font-medium">{bp.label}</span>
            </button>
          ))}
        </div>
        
        <RowEditor
          rows={layout}
          onChange={(newRows) => updateLayout(newRows, [activeBP])}
        />
      </div>
    );
  };
  
  return (
    <div className="field-wrapper">
      {label && (
        <label className={`block mb-2 ${typography.label} ${textColors.secondary}`}>
          {label}
          {infoText ? <span className="ml-2 align-middle"><InfoIcon text={infoText} /></span> : null}
        </label>
      )}
      
      {/* Mode selector */}
      <ScreenSizeModeSelector
        mode={activeMode}
        onChange={switchMode}
        disabled={false}
      />
      
      {renderEditor()}
      
      {help && <div className={`mt-2 ${typography.caption} ${textColors.tertiary}`}>{help}</div>}
    </div>
  );
};

